'use client';

import React, { useState } from 'react';
import * as Yup from 'yup';
import Section from '@repo/ui/src/components/Section';
import Button from '@repo/ui/src/components/Button';
import { Markdown } from '@repo/ui/src/components/Markdown';
import { ImageProps } from '@repo/ui/src/types/image';
import { LinkProps } from '@repo/ui/src/types/link';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import Input from '@repo/ui/src/components/Form/Input';
import { Formik, Form } from 'formik';
import { useApplyCouponToBasket } from '@hooks/basket/useApplyCouponToBasket';
import { useValidateGiftCardVIPAccess } from '@hooks/basket/useValidateGiftCardVIPAccess';
import useBasket from '@hooks/basket/useBasket';
import { prependSlash } from '@repo/ui/src/components/Anchor/helpers';

interface InputProps {
  label: string;
  placeholder?: string;
  errorText?: string;
}

interface CodeRedemptionButtonProps {
  text: string;
  display: {
    $ref: 'https://traeger.com/partials.json#/definitions/buttonDisplay';
  };
  ariaLabel: string;
}

interface CodeRedemptionProps {
  backgroundImage?: ImageProps;
  title: string;
  description?: string;
  couponType: 'giftCard' | 'vip';
  inputs: Array<InputProps>;
  button: CodeRedemptionButtonProps;
  link?: LinkProps;
}

const CodeRedemption = ({
  backgroundImage,
  title,
  description,
  couponType,
  inputs,
  button,
  link,
}: CodeRedemptionProps) => {
  const [submissionError, setSubmissionError] = useState('');
  const { trigger: applyCoupon, isMutating: isApplyingCoupon } = useApplyCouponToBasket();
  const { trigger: validateGiftCard, isMutating: isValidatingGiftCard } =
    useValidateGiftCardVIPAccess();
  const { data: basket } = useBasket();

  const backgroundImageUrl = backgroundImage ? buildImageSrc(backgroundImage, 1200, 16, 9) : '';

  const validationSchema = Yup.object().shape(
    inputs.reduce(
      (acc, input, index) => {
        acc[`input_${index}`] = Yup.string().required(input.errorText || 'This field is required');
        return acc;
      },
      {} as Record<string, Yup.StringSchema>,
    ),
  );

  const initialValues = inputs.reduce(
    (acc, _, index) => {
      acc[`input_${index}`] = '';
      return acc;
    },
    {} as Record<string, string>,
  );

  const runSuccessPath = () => {
    const redirectUrl = link?.link || link?.externalLink;
    if (redirectUrl) {
      // need for client side exception error
      window.location.href = prependSlash(redirectUrl);
    }
  };

  const handleSubmit = async (values: Record<string, string>) => {
    setSubmissionError('');

    if (!!basket?.vip) {
      return runSuccessPath();
    }

    const code = values.input_0.trim();

    try {
      if (couponType === 'giftCard') {
        await validateGiftCard(code);
      } else {
        await applyCoupon(code);
      }
      runSuccessPath();
    } catch (error) {
      console.error('Code redemption error:', error);
      setSubmissionError(inputs[0].errorText || 'An error occurred. Please try again.');
    }
  };

  return (
    <Section
      gutterX
      gutterY
      align="center"
      backgroundImageUrl={backgroundImageUrl}
      className="relative py-20 h-screen"
    >
      {backgroundImageUrl && <div className="absolute inset-0 bg-black bg-opacity-50" />}
      <div className="max-w-[500px] h-full m-auto flex flex-col items-center justify-center relative z-10">
        <h2 className="tra-text-web-heading-3 mb-6 text-center text-white">{title}</h2>

        {description && (
          <div className="mb-8">
            <Markdown className="text-center text-white">{description}</Markdown>
          </div>
        )}

        <div className="w-full">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validate={() => ({})}
            validateOnChange={false}
            validateOnBlur={false}
          >
            {({ isSubmitting }) => (
              <Form className="space-y-6">
                {inputs.map((input, index) => (
                  <Input
                    key={`input_${index}`}
                    name={`input_${index}`}
                    label={input.label}
                    placeholder={input.placeholder}
                    required
                    autoFocus
                    darkBackground
                    className="w-full"
                    inputStyles="bg-white text-neutral-700"
                  />
                ))}

                <Button
                  type="submit"
                  size="large"
                  full
                  disabled={isSubmitting || isApplyingCoupon || isValidatingGiftCard}
                  className="mt-6"
                  aria-label={button.ariaLabel}
                >
                  {button.text}
                </Button>

                {submissionError && (
                  <div className="bg-red-600 text-white px-4 py-3 rounded-md text-sm text-center">
                    {submissionError}
                  </div>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </Section>
  );
};

export default CodeRedemption;
